import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/TimberClass';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const TIMBER_CLASS_API_REDUCER_KEY = 'timber-classes';
export const TIMBER_CLASS_PATH = 'timber-classes';

export const timberClassApi = createApi({
    reducerPath: TIMBER_CLASS_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['TimberClass', 'TimberClasses'],
    endpoints: (builder) => ({
        getTimberClassList: builder.query<PaginationResult<Entity.TimberClass>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ TIMBER_CLASS_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberClasses'],
        }),
        getTimberClass: builder.query<Entity.TimberClass, Entity.PrimaryTimberClass>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = timberClassNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ TIMBER_CLASS_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['TimberClass'],
        }),
        getTimberClassMultiple: builder.query<Entity.TimberClass, Entity.PrimaryTimberClass[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/ids${Entity.PrimaryTimberClass.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberClasses'],
        }),
        getTimberClassPresentation: builder.query<PaginationResult<Entity.TimberClass>, Entity.PresentationTimberClassRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ TIMBER_CLASS_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['TimberClasses'],
        }),
        
        insertTimberClass: builder.mutation<Entity.TimberClass, Entity.CreateTimberClass>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    timberClassApi.util.upsertQueryData('getTimberClass', Entity.PrimaryTimberClass.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['TimberClasses'],
        }),
        insertMultipleTimberClass: builder.mutation<Entity.TimberClass[], Entity.CreateTimberClass[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            timberClassApi.util.upsertQueryData('getTimberClass', Entity.PrimaryTimberClass.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['TimberClasses'],
        }),
        updateTimberClass: builder.mutation<Entity.TimberClass, Entity.UpdateEntityTimberClass>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['TimberClasses'],
        }),
        updateMultipleTimberClass: builder.mutation<Entity.TimberClass[], Entity.UpdateEntityTimberClass[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['TimberClasses'],
        }),
        deleteTimberClass: builder.mutation<Entity.TimberClass, Entity.TimberClass>({
            query: (toDelete) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/${Entity.PrimaryTimberClass.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberClasses'],
        }),
        deleteMultipleTimberClass: builder.mutation<Entity.TimberClass[], Entity.PrimaryTimberClass[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_CLASS_PATH }/batch${Entity.PrimaryTimberClass.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberClasses'],
        }),
    }),
})

export const [timberClassNormalizer, timberClassNormalizerMiddleware] = createNormalizationMiddleware(TIMBER_CLASS_API_REDUCER_KEY, timberClassApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetTimberClassListQuery,
    useGetTimberClassMultipleQuery,
    useGetTimberClassPresentationQuery,
    useGetTimberClassQuery,
    useInsertTimberClassMutation,
    useInsertMultipleTimberClassMutation,
    useUpdateTimberClassMutation,
    useUpdateMultipleTimberClassMutation,
    useDeleteTimberClassMutation,
    useDeleteMultipleTimberClassMutation,
    
} = timberClassApi
